import React from 'react';
import { FiCheck, FiTarget, FiUsers, FiMessageSquare, FiSettings, FiEdit3 } from 'react-icons/fi';

export function ContentReviewStep({ data }) {
  const getVerticalLabel = (value) => {
    const verticals = {
      // Self-Service Operations
      'parking': 'Parking (Off-Street)',
      'fuel': 'Fuel & Convenience',
      'vending': 'Vending',
      'carwash': 'Car Wash',
      'laundry': 'Laundry',
      'evcharging': 'EV Charging',
      'transit': 'Mass Transit/Ticketing',
      'amusement': 'Amusement/Recreation',
      
      // Food & Beverage
      'qsr': 'Quick Service Restaurants',
      'specialty-food': 'Specialty Food & Beverage',
      
      // Retail & Commerce
      'fashion-apparel': 'Fashion & Apparel',
      'beauty-cosmetics': 'Beauty & Cosmetics',
      'electronics-tech': 'Electronics & Technology',
      'home-garden': 'Home & Garden',
      'pet-services': 'Pet Services',
      'sporting-goods': 'Sporting Goods',
      'health-wellness': 'Health & Wellness',
      'jewelry-accessories': 'Jewelry & Accessories',
      'books-stationery': 'Books & Stationery',
      'hobby-crafts': 'Hobby & Crafts'
    };
    return verticals[value] || value;
  };

  const getPersonaLabel = (value) => {
    const personas = {
      'business-buyer': 'Business Buyers',
      'technical-buyer': 'Technical Buyers',
      'channel-partner': 'Channel Partners'
    };
    return personas[value] || value;
  };

  const getGeographicLabel = (value) => {
    const options = {
      'usa': 'United States',
      'eu': 'Europe',
      'apac': 'APAC',
      'br': 'Brazil',
      'global': 'Global'
    };
    return options[value] || value;
  };

  const getThemeLabel = (value) => {
    const themes = {
      'agitate': 'Agitate - Problem-focused content',
      'educate': 'Educate - Solution-focused educational content',
      'engage': 'Engage - Partnership and collaboration-focused'
    };
    return themes[value] || value;
  };

  const getFormatLabel = (value) => {
    const formats = {
      'blog-post': 'Blog Post (800-1500 words)',
      'white-paper': 'White Paper (2000+ words)',
      'case-study': 'Case Study',
      'email-campaign': 'Email Campaign (3-5 emails)',
      'social-media': 'Social Media Content',
      'sales-enablement': 'Sales Enablement Materials'
    };
    return formats[value] || value;
  };

  const sections = [
    {
      title: 'Campaign Setup',
      icon: FiTarget,
      items: [
        { label: 'Business Vertical', value: getVerticalLabel(data.vertical) },
        { label: 'Target Audience', value: getPersonaLabel(data.audiencePersona) },
        { label: 'Geographic Focus', value: getGeographicLabel(data.geographicFocus) },
        { label: 'Country', value: data.country || 'Not specified' },
        { label: 'Campaign Objective', value: data.campaignObjective?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) }
      ]
    },
    {
      title: 'Ideal Customer Profile',
      icon: FiUsers,
      items: [
        { label: 'Company Size', value: data.companySize?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
        { label: 'Revenue Range', value: data.revenueRange?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
        { label: 'Geographic Footprint', value: data.geographicFootprint?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
        { label: 'Technology Maturity', value: data.technologyMaturity?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
        { label: 'Growth Stage', value: data.growthStage?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
        { label: 'Key Decision Factors', value: data.keyDecisionFactors?.join(', ') || 'None selected' }
      ]
    },
    {
      title: 'Content Strategy',
      icon: FiMessageSquare,
      items: [
        { label: 'Content Theme', value: getThemeLabel(data.contentTheme) },
        { label: 'Content Format', value: getFormatLabel(data.contentFormat) },
        { label: 'Selected Pain Points', value: `${(data.selectedPainPoints?.length || 0) + (data.customPainPoints?.length || 0)} pain points selected` }
      ]
    },
    {
      title: 'Advanced Settings',
      icon: FiSettings,
      items: [
        { label: 'Compliance Focus', value: data.complianceMessaging?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified' },
        { label: 'Competitive Positioning', value: data.competitivePositioning?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified' },
        { label: 'Technical Depth', value: data.technicalDepth?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified' },
        { label: 'Call-to-Action', value: data.callToAction?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified' },
        { label: 'Brand Voice', value: data.brandVoice?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified' }
      ]
    }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Review Your Content Strategy</h2>
        <p className="text-gray-600 mb-8">
          Review your marketing content configuration before generating AI-powered content. You can go back to any previous step to make changes.
        </p>
      </div>

      {/* Configuration Summary */}
      <div className="space-y-6">
        {sections.map((section, index) => {
          const IconComponent = section.icon;
          return (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <IconComponent className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="ml-3 text-lg font-medium text-gray-900">{section.title}</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex justify-between py-2">
                    <span className="text-sm font-medium text-gray-600">{item.label}:</span>
                    <span className="text-sm text-gray-900 text-right max-w-60 truncate" title={item.value}>
                      {item.value || 'Not specified'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pain Points Summary */}
      {(data.selectedPainPoints?.length > 0 || data.customPainPoints?.length > 0) && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <FiEdit3 className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="ml-3 text-lg font-medium text-gray-900">Selected Pain Points</h3>
          </div>
          
          {data.selectedPainPoints?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Industry Pain Points:</h4>
              <div className="flex flex-wrap gap-2">
                {data.selectedPainPoints.map((painPoint, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {painPoint}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {data.customPainPoints?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Pain Points:</h4>
              <div className="flex flex-wrap gap-2">
                {data.customPainPoints.map((painPoint, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                  >
                    {painPoint}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Additional Notes */}
      {data.notes && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Additional Notes</h3>
          <p className="text-gray-600 text-sm whitespace-pre-wrap">{data.notes}</p>
        </div>
      )}

      {/* Generation Preview */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <div className="flex items-center mb-4">
          <FiCheck className="w-6 h-6 text-green-600 mr-3" />
          <h3 className="text-lg font-medium text-green-900">Ready to Generate Content</h3>
        </div>
        <div className="text-sm text-green-700 space-y-2">
          <p>
            <strong>Content Type:</strong> {getFormatLabel(data.contentFormat)}
          </p>
          <p>
            <strong>Target Audience:</strong> {getPersonaLabel(data.audiencePersona)} in {getVerticalLabel(data.vertical)}
          </p>
          <p>
            <strong>Approach:</strong> {getThemeLabel(data.contentTheme)}
          </p>
          <p>
            <strong>Estimated Generation Time:</strong> 1-4 minutes
          </p>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-yellow-800 mb-2">Before You Generate</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Review all settings above for accuracy</li>
          <li>• Ensure your pain points align with your target audience</li>
          <li>• Verify the content format matches your campaign needs</li>
          <li>• Use the back button to modify any settings if needed</li>
        </ul>
      </div>
    </div>
  );
}